import React, { useState, useMemo } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface MerchantOption {
  id: string;
  name: string;
}

interface BrandsListProps {
  service: TableService; // New prop
  role?: 'admin' | 'operator';
  showMerchantColumn?: boolean;
  merchants?: MerchantOption[];
}

interface BrandRow {
  id: string;
  name: string;
  merchant_id?: string;
  description?: string | null;
  created_at?: string;
  [key: string]: unknown;
}

const BrandsList: React.FC<BrandsListProps> = ({
  service, // New prop
  role = 'admin',
  showMerchantColumn = false,
  merchants = []
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const columns = useMemo(() => {
    const cols: unknown[] = [
      {
        nameKey: `models.brand.name`,
        persist: true,
        sortable: true,
        sortFieldName: 'brands.name',
        wrap: true,
        cell: ({ id, name }: BrandRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/brands/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`/${role}/brands/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      }
    ]

    // Operator 需要顯示 merchant 欄位
    if (showMerchantColumn && merchants.length > 0) {
      cols.push({
        nameKey: `models.brand.merchant`,
        wrap: true,
        selector: ({ merchant_id }: BrandRow) => {
          return merchants.find((merchant) => merchant.id === merchant_id)?.name
        },
      })
    }

    cols.push(
      {
        nameKey: `models.brand.description`,
        sortable: false,
        wrap: true,
        selector: ({ description }: BrandRow) => description,
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'brands.id',
        wrap: true,
        selector: ({ created_at }: BrandRow) => created_at,
      }
    )

    return cols
  }, [role, showMerchantColumn, merchants])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = []

    // Operator 需要 merchant 篩選器
    if (showMerchantColumn && merchants.length > 0) {
      rules.push({
        label: t(`shared.brands.list.filters.merchant`),
        component: MultipleValue,
        inputName: 'brands.merchant_id',
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.brands.list.filter_tooltips.merchant.title'),
          content: t('shared.brands.list.filter_tooltips.merchant.content'),
        },
      })
    }

    return rules
  }, [t, role, showMerchantColumn, merchants])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'prefix',
        searchFields: [t('models.brand.name')],
      }}
      columnsEditable={true}
      tableId={`${role.charAt(0).toUpperCase() + role.slice(1)}BrandsTable`}
      defaultQuery={{
        sorting_field: 'brands.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.brands.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/brands/list#features`}
    />
  )
}

export default BrandsList
